import React, { useState } from "react";
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Link,
  Button,
  Collapse,
  Alert,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CardMedia from "@mui/material/CardMedia";
import Div from "@jumbo/shared/Div";
import { ASSET_CODINGPAL } from "app/utils/constants/paths";
import { Form, Formik, Field } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import authServices from "app/services/auth-services";
import { ROUTES } from "app/utils/constants/routeConstants";
import { LoadingButton } from "@mui/lab";
import useMetaTitle from "app/hooks/useMetaTitle";

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setError(null);
  };

  const handleResetPassword = async (email, setSubmitting) => {
    try {
      await authServices.resetPassword(email);
      navigate(ROUTES.USER_VERIFY_ACCOUNT, {
        state: { source: "forgotPassword", email },
      });
    } catch (err) {
      setError(err.response?.data?.title);
      setOpenAlert(true);
      setSubmitting(false);
    }
  };

  useMetaTitle({
    title: "Forgot Password",
  });

  return (
    <Div
      sx={{
        flex: 1,
        flexWrap: "wrap",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: (theme) => theme.spacing(4),
      }}
    >
      <Card sx={{ maxWidth: "100%", width: 360 }}>
        <Div sx={{ position: "relative", height: "250px" }}>
          <CardMedia component="img" alt="Background" height="200" />
          <Div
            sx={{
              flex: 1,
              inset: 0,
              position: "absolute",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              justifyContent: "space-around",
              backgroundColor: "#222d45",
              p: (theme) => theme.spacing(3),
            }}
          >
            <Div
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <Link href="#" underline="none" sx={{ display: "inline-flex" }}>
                <img
                  src={`${ASSET_CODINGPAL}/codingpal.jpg`}
                  alt="CodingPal"
                  width={40}
                  height={40}
                />
              </Link>
              <Typography
                variant="h2"
                color="white"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginBottom: 0,
                }}
              >
                CodingPal
              </Typography>
            </Div>
            <Div
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: "common.white",
                  fontSize: "1.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                Forgot Password
              </Typography>
              <Typography variant="body1" color="common.white" mb={2}>
                We'll send you a link to reset your password. Enter your email
                and we’ll take care of the rest.
              </Typography>
            </Div>
          </Div>
        </Div>
        <CardContent>
          <Collapse in={openAlert}>
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleCloseAlert}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 1 }}
            >
              {error}
            </Alert>
          </Collapse>
          <Formik
            validateOnChange={true}
            initialValues={{ email: "" }}
            validationSchema={validationSchema}
            onSubmit={(data, { setSubmitting }) => {
              setSubmitting(true);
              handleResetPassword(data.email, setSubmitting);
            }}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form style={{ textAlign: "left" }} noValidate autoComplete="off">
                <Div sx={{ mb: 3, mt: 1 }}>
                  <Field
                    as={TextField}
                    fullWidth
                    id="email"
                    name="email"
                    label="E-mail Address"
                    type="email"
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                </Div>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    mb: 2,
                    backgroundColor: "#222d45",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#222d45",
                    },
                  }}
                  loading={isSubmitting}
                >
                 Reset My Password
                </LoadingButton>
              </Form>
            )}
          </Formik>
          <Div
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{
                color: "#222d45",
                textTransform: "none",
              }}
              onClick={() => {
                navigate(ROUTES.USER_LOGIN);
              }}
            >
              Sign In
            </Button>
            <Button
              sx={{
                color: "#222d45",
                textTransform: "none",
              }}
              onClick={() => {
                navigate(ROUTES.USER_SIGNUP);
              }}
            >
              Sign Up
            </Button>
          </Div>
        </CardContent>
      </Card>
    </Div>
  );
};

export default ForgotPassword;
